/**
 * Backend cloud SIMULÉ (dev/e2e via `?mockcloud=1`) — aucune dépendance réseau ni Firebase.
 * Sépare volontairement deux espaces pour reproduire « appareil » vs « serveur » :
 * - la SESSION (qui est connecté sur CET appareil) vit dans localStorage → effacée en
 *   simulant un nouvel appareil (`localStorage.clear()`) ;
 * - la BASE (utilisateurs + sauvegardes) vit dans sessionStorage → survit au `clear()` et au
 *   reload, comme un serveur. Un test peut ainsi vérifier la restauration inter-appareils.
 */
import type { AuthResult, CloudBackend, CloudUser, SavePayload } from './types';

interface Db {
  users: Record<string, { uid: string; password: string }>; // clé = email normalisé
  saves: Record<string, SavePayload>; // clé = uid
}

const DB_KEY = '__mockcloud_db';
const SESSION_KEY = '__mockcloud_session';

function loadDb(): Db {
  try {
    return JSON.parse(sessionStorage.getItem(DB_KEY) ?? '') as Db;
  } catch {
    return { users: {}, saves: {} };
  }
}
function saveDb(db: Db): void {
  sessionStorage.setItem(DB_KEY, JSON.stringify(db));
}
const norm = (e: string): string => e.trim().toLowerCase();

export class MockCloudBackend implements CloudBackend {
  private cb: ((u: CloudUser | null) => void) | null = null;

  private session(): CloudUser | null {
    try {
      const s = localStorage.getItem(SESSION_KEY);
      if (!s) return null;
      const { uid, email } = JSON.parse(s) as CloudUser;
      return uid && email ? { uid, email } : null;
    } catch {
      return null;
    }
  }
  private setSession(u: CloudUser | null): void {
    if (u) localStorage.setItem(SESSION_KEY, JSON.stringify(u));
    else localStorage.removeItem(SESSION_KEY);
    this.cb?.(u);
  }

  onAuth(cb: (u: CloudUser | null) => void): void {
    this.cb = cb;
    cb(this.session());
  }
  currentUser(): CloudUser | null {
    return this.session();
  }

  signUp(email: string, password: string): Promise<AuthResult> {
    const e = norm(email);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return Promise.resolve({ ok: false, error: "L'email n'est pas valide." });
    if (password.length < 8) return Promise.resolve({ ok: false, error: 'Mot de passe : 8 caractères minimum.' });
    const db = loadDb();
    if (db.users[e]) return Promise.resolve({ ok: false, error: 'Cet email a déjà un compte.' });
    const uid = 'u' + Math.random().toString(36).slice(2, 12);
    db.users[e] = { uid, password };
    saveDb(db);
    this.setSession({ uid, email: e });
    return Promise.resolve({ ok: true });
  }
  signIn(email: string, password: string): Promise<AuthResult> {
    const e = norm(email);
    const db = loadDb();
    const u = db.users[e];
    if (!u || u.password !== password)
      return Promise.resolve({ ok: false, error: 'Email ou mot de passe incorrect.' });
    this.setSession({ uid: u.uid, email: e });
    return Promise.resolve({ ok: true });
  }
  signOut(): Promise<void> {
    this.setSession(null);
    return Promise.resolve();
  }
  sendPasswordReset(_email: string): Promise<AuthResult> {
    // Comme Firebase (protection anti-énumération) : succès quel que soit l'email.
    void _email;
    return Promise.resolve({ ok: true });
  }
  saveData(data: Record<string, string>): Promise<void> {
    const u = this.session();
    if (!u) return Promise.resolve();
    const db = loadDb();
    db.saves[u.uid] = { data, updatedAt: Date.now() };
    saveDb(db);
    return Promise.resolve();
  }
  loadData(): Promise<SavePayload | null> {
    const u = this.session();
    if (!u) return Promise.resolve(null);
    return Promise.resolve(loadDb().saves[u.uid] ?? null);
  }
}
